import { useCallback, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  AdminHouseholdDTO,
  AdminUserRole,
  AdminUserRowDTO,
  AttachHouseholdMemberPayload,
  HouseholdMemberRole,
  RemoveAdminUserHouseholdDTO,
  UpdateAdminUserRolePayload,
} from '../types/Admin';
import { authFetch } from '../lib/authFetch';

type ParsedResponse = {
  data: unknown;
  message: string;
};

const parseResponse = async (res: Response): Promise<ParsedResponse> => {
  const raw = await res.text();
  if (!raw) return { data: null, message: '' };

  try {
    const parsed = JSON.parse(raw) as { message?: string };
    return {
      data: parsed,
      message: typeof parsed.message === 'string' ? parsed.message.trim() : '',
    };
  } catch {
    return { data: null, message: raw.trim() };
  }
};

export const useAdminUsers = () => {
  const { auth } = useAuth();
  const token = auth.token;

  const getAuthHeaders = useCallback((): HeadersInit => {
    if (!token) {
      throw new Error('You must be signed in to access admin users.');
    }

    return {
      Authorization: `Bearer ${token}`,
    };
  }, [token]);

  const loadUsers = useCallback(async (): Promise<AdminUserRowDTO[]> => {
    const res = await authFetch('/api/admin/users', {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    const { data, message } = await parseResponse(res);

    if (!res.ok) {
      throw new Error(message || `Request failed (${res.status})`);
    }

    if (!Array.isArray(data)) {
      throw new Error('Invalid response payload');
    }

    return data as AdminUserRowDTO[];
  }, [getAuthHeaders]);

  const updateUserRole = useCallback(async (userId: string, role: AdminUserRole): Promise<void> => {
    const payload: UpdateAdminUserRolePayload = { role };

    const res = await authFetch(`/api/admin/users/${encodeURIComponent(userId)}/role`, {
      method: 'PATCH',
      headers: {
        ...getAuthHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    const { message } = await parseResponse(res);

    if (!res.ok) {
      throw new Error(message || `Request failed (${res.status})`);
    }
  }, [getAuthHeaders]);

  const attachMember = useCallback(async (
    householdId: string,
    userId: string,
    roleInHousehold?: HouseholdMemberRole
  ): Promise<void> => {
    const payload: AttachHouseholdMemberPayload = {
      userId,
      ...(roleInHousehold ? { roleInHousehold } : {}),
    };

    const res = await authFetch(`/api/admin/households/${encodeURIComponent(householdId)}/members`, {
      method: 'POST',
      headers: {
        ...getAuthHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    const { message } = await parseResponse(res);

    if (!res.ok) {
      throw new Error(message || `Request failed (${res.status})`);
    }
  }, [getAuthHeaders]);

  const createHouseholdForUser = useCallback(async (
    createdForUserId: string,
    name?: string,
    roleInHousehold?: HouseholdMemberRole
  ): Promise<AdminHouseholdDTO> => {
    const res = await authFetch('/api/admin/households', {
      method: 'POST',
      headers: {
        ...getAuthHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        createdForUserId,
        ...(name ? { name } : {}),
        ...(roleInHousehold ? { roleInHousehold } : {}),
      }),
    });

    const { data, message } = await parseResponse(res);
    if (!res.ok) {
      throw new Error(message || `Request failed (${res.status})`);
    }

    if (
      !data ||
      typeof data !== 'object' ||
      !('household' in data) ||
      !data.household ||
      typeof (data as AdminHouseholdDTO).household.id !== 'string'
    ) {
      throw new Error('Invalid response payload');
    }

    return data as AdminHouseholdDTO;
  }, [getAuthHeaders]);

  const removeUserFromHousehold = useCallback(async (userId: string): Promise<RemoveAdminUserHouseholdDTO> => {
    const res = await authFetch(`/api/admin/users/${encodeURIComponent(userId)}/household`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });

    const { data, message } = await parseResponse(res);
    if (!res.ok) {
      throw new Error(message || `Request failed (${res.status})`);
    }

    if (
      !data ||
      typeof data !== 'object' ||
      typeof (data as RemoveAdminUserHouseholdDTO).removed !== 'boolean' ||
      !Object.prototype.hasOwnProperty.call(data, 'householdId')
    ) {
      throw new Error('Invalid response payload');
    }

    return data as RemoveAdminUserHouseholdDTO;
  }, [getAuthHeaders]);

  return useMemo(
    () => ({
      loadUsers,
      updateUserRole,
      attachMember,
      createHouseholdForUser,
      removeUserFromHousehold,
    }),
    [attachMember, createHouseholdForUser, loadUsers, removeUserFromHousehold, updateUserRole]
  );
};

export default useAdminUsers;
